import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Assignment as InitiativeIcon,
  Event as EventIcon,
  Task as TaskIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { Contact } from '../contacts/ContactModal';
import { Initiative, Event, Task } from '../../types/database';

interface ActivityItem {
  id: string;
  type: 'contact' | 'initiative' | 'event' | 'task';
  action: string;
  name: string;
  timestamp: string;
  details?: string;
  metadata?: any;
}

export function ActivityList() {
  const navigate = useNavigate();
  const location = useLocation();
  const db = useDatabase();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedActivity, setSelectedActivity] = useState<ActivityItem | null>(null);
  const [filterExpanded, setFilterExpanded] = useState(false);
  
  // Get type filter from URL, default to 'all' if no filter is set
  const typeFilter = new URLSearchParams(location.search).get('type') || 'all';
  
  // Get date range filter from URL, default to 'all' if no filter is set
  const dateRangeFilter = new URLSearchParams(location.search).get('date_range') || 'all';
  
  // Get event subtype filter from URL, default to 'all' if no filter is set
  const eventSubtypeFilter = new URLSearchParams(location.search).get('event_subtype') || 'all';

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load contacts, initiatives, events, and tasks
      const [contactsResponse, initiativesResponse, eventsResponse, tasksResponse] = await Promise.all([
        db.getContacts(),
        db.getInitiatives(),
        db.getEvents(),
        db.getTasks(),
      ]);

      if (contactsResponse.error) throw contactsResponse.error;
      if (initiativesResponse.error) throw initiativesResponse.error;
      if (eventsResponse.error) throw eventsResponse.error;
      if (tasksResponse.error) throw tasksResponse.error;

      const contacts = contactsResponse.data || [];
      const initiatives = initiativesResponse.data || [];
      const events = eventsResponse.data || [];
      const tasks = tasksResponse.data || [];

      // Combine and sort activities
      const activityItems: ActivityItem[] = [
        ...contacts.map((c: Contact) => ({
          id: c.id,
          type: 'contact' as const,
          action: c.updated_at && c.updated_at !== c.created_at ? 'Updated contact' : 'Added contact',
          name: c.name,
          timestamp: c.updated_at || c.created_at || new Date().toISOString(),
        })),
        ...initiatives.map((i: Initiative) => ({
          id: i.id,
          type: 'initiative' as const,
          action: i.updated_at && i.updated_at !== i.created_at ? 'Updated initiative' : 'Created initiative',
          name: i.name,
          timestamp: i.updated_at || i.created_at || new Date().toISOString(),
        })),
        ...events.map((e: Event) => ({
          id: e.id,
          type: 'event' as const,
          action: 'Created event',
          name: e.title,
          timestamp: e.created_at || new Date().toISOString(),
          metadata: {
            contact_id: e.contact_id,
            type: e.metadata.type
          }
        })),
        ...tasks.map((t: Task) => ({
          id: t.id,
          type: 'task' as const,
          action: t.updated_at && t.updated_at !== t.created_at ? 'Updated task' : 'Created task',
          name: t.title,
          timestamp: t.updated_at || t.created_at || new Date().toISOString(),
          metadata: {
            status: t.status,
            priority: t.priority
          }
        })),
      ]
        .filter(item => item.timestamp)
        .sort((a, b) => new Date(b.timestamp!).getTime() - new Date(a.timestamp!).getTime());

      setActivities(activityItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const handleTypeChange = (newType: string) => {
    const params = new URLSearchParams(location.search);
    if (newType === 'all') {
      params.delete('type');
    } else {
      params.set('type', newType);
    }
    
    const queryString = params.toString();
    navigate(`/activity${queryString ? `?${queryString}` : ''}`);
  };

  const handleDateRangeChange = (newDateRange: string) => {
    const params = new URLSearchParams(location.search);
    if (newDateRange === 'all') {
      params.delete('date_range');
    } else {
      params.set('date_range', newDateRange);
    }
    
    const queryString = params.toString();
    navigate(`/activity${queryString ? `?${queryString}` : ''}`);
  };

  const handleEventSubtypeChange = (newEventSubtype: string) => {
    const params = new URLSearchParams(location.search);
    if (newEventSubtype === 'all') {
      params.delete('event_subtype');
    } else {
      params.set('event_subtype', newEventSubtype);
    }
    
    const queryString = params.toString();
    navigate(`/activity${queryString ? `?${queryString}` : ''}`);
  };

  // Helper function to get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (typeFilter !== 'all') count++;
    if (dateRangeFilter !== 'all') count++;
    if (eventSubtypeFilter !== 'all') count++;
    return count;
  };

  // Helper function to clear all filters
  const clearAllFilters = () => {
    navigate('/activity');
  };

  // Helper function to get filter display name
  const getFilterDisplayName = (type: string, value: string) => {
    const displayNames: { [key: string]: { [key: string]: string } } = {
      type: {
        all: 'All',
        contact: 'Contacts',
        initiative: 'Initiatives',
        event: 'Events',
        task: 'Tasks',
      },
      date_range: {
        all: 'All Time',
        today: 'Today',
        yesterday: 'Yesterday',
        this_week: 'This Week',
        last_week: 'Last Week',
        this_month: 'This Month',
        last_month: 'Last Month',
      },
      event_subtype: {
        all: 'All Event Types',
        quant_update: 'Quant Updates',
        meeting: 'Meetings',
        call: 'Calls',
        email: 'Emails',
        other: 'Other',
      },
    };
    return displayNames[type]?.[value] || value;
  };

  // Helper function to check if activity matches date range
  const matchesDateRange = (timestamp: string, dateRange: string) => {
    if (dateRange === 'all') return true;
    
    const activityDate = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    
    const activityDateOnly = new Date(activityDate.getFullYear(), activityDate.getMonth(), activityDate.getDate());
    
    switch (dateRange) {
      case 'today':
        return activityDateOnly.getTime() === today.getTime();
      case 'yesterday':
        return activityDateOnly.getTime() === yesterday.getTime();
      case 'this_week':
        return activityDateOnly >= startOfWeek && activityDateOnly <= today;
      case 'last_week':
        return activityDateOnly >= startOfLastWeek && activityDateOnly < startOfWeek;
      case 'this_month':
        return activityDate >= startOfMonth;
      case 'last_month':
        return activityDate >= startOfLastMonth && activityDate < startOfMonth;
      default:
        return true;
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, activity: ActivityItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedActivity(activity);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActivity(null);
  };

  const handleViewDetails = () => {
    if (selectedActivity) {
      if (selectedActivity.type === 'event') {
        // For events, navigate to the contact's page
        navigate(`/contacts/${selectedActivity.metadata?.contact_id}`);
      } else {
        navigate(`/${selectedActivity.type}s/${selectedActivity.id}`);
      }
    }
    handleMenuClose();
  };

  // Filter activities based on current filters
  const filteredActivities = activities.filter(activity => {
    // Apply type filter
    const typeMatch = typeFilter === 'all' || activity.type === typeFilter;
    
    // Apply date range filter
    const dateRangeMatch = matchesDateRange(activity.timestamp, dateRangeFilter);
    
    // Apply event subtype filter (only for events)
    const eventSubtypeMatch = eventSubtypeFilter === 'all' || 
      (activity.type === 'event' && 
       activity.metadata?.type === eventSubtypeFilter);
    
    return typeMatch && dateRangeMatch && eventSubtypeMatch;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Activity">
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, backgroundColor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Mobile-optimized Header */}
        <Box sx={{ mb: 2 }}>
          {/* Filter toggle button */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center', 
            mb: 2,
            gap: 1
          }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              endIcon={<ExpandMoreIcon sx={{ 
                transform: filterExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} />}
              onClick={() => setFilterExpanded(true)}
              sx={{ 
                borderRadius: 2,
                height: 44,
                minWidth: { xs: 'auto', sm: 120 },
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '0.9375rem' }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Filters</Box>
              <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Filter</Box>
            </Button>
          </Box>
          
          {/* Active filter chips - mobile optimized */}
          {getActiveFilterCount() > 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              mb: 1
            }}>
              {typeFilter !== 'all' && (
                <Chip
                  label={`Type: ${getFilterDisplayName('type', typeFilter)}`}
                  size="small"
                  onDelete={() => handleTypeChange('all')}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
              {dateRangeFilter !== 'all' && (
                <Chip
                  label={`Date: ${getFilterDisplayName('date_range', dateRangeFilter)}`}
                  size="small"
                  onDelete={() => handleDateRangeChange('all')}
                  color="secondary"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
              {eventSubtypeFilter !== 'all' && (
                <Chip
                  label={`Event: ${getFilterDisplayName('event_subtype', eventSubtypeFilter)}`}
                  size="small"
                  onDelete={() => handleEventSubtypeChange('all')}
                  color="success"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
              <Chip
                label="Clear All"
                size="small"
                onClick={clearAllFilters}
                onDelete={clearAllFilters}
                color="default"
                variant="outlined"
                icon={<ClearIcon />}
                sx={{ 
                  fontSize: '0.75rem',
                  height: 28,
                  '& .MuiChip-label': {
                    px: 1
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Mobile-optimized Filter Panel */}
        <Collapse in={filterExpanded}>
          <Paper 
            sx={{ 
              mb: 2,
              boxShadow: 1,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Filter Header with Close Button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Filters
              </Typography>
              <IconButton
                size="small"
                onClick={() => setFilterExpanded(false)}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    backgroundColor: 'action.selected',
                    color: 'text.primary'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {/* Filter Content */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={{ xs: 2, sm: 3 }}>
                {/* Type Filter */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    Activity Type
                  </Typography>
                  <ToggleButtonGroup
                    value={typeFilter}
                    exclusive
                    onChange={(_, value) => value && handleTypeChange(value)}
                    size="small"
                    sx={{ 
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        minHeight: 32
                      }
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="contact">Contacts</ToggleButton>
                    <ToggleButton value="initiative">Initiatives</ToggleButton>
                    <ToggleButton value="event">Events</ToggleButton>
                    <ToggleButton value="task">Tasks</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Date Range Filter */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    Date Range
                  </Typography>
                  <ToggleButtonGroup
                    value={dateRangeFilter}
                    exclusive
                    onChange={(_, value) => value && handleDateRangeChange(value)}
                    size="small"
                    sx={{ 
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        minHeight: 32
                      }
                    }}
                  >
                    <ToggleButton value="all">All Time</ToggleButton>
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="yesterday">Yesterday</ToggleButton>
                    <ToggleButton value="this_week">This Week</ToggleButton>
                    <ToggleButton value="last_week">Last Week</ToggleButton>
                    <ToggleButton value="this_month">This Month</ToggleButton>
                    <ToggleButton value="last_month">Last Month</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Event Subtype Filter - Only show when type is 'event' or 'all' */}
                {(typeFilter === 'event' || typeFilter === 'all') && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                      Event Type
                    </Typography>
                    <ToggleButtonGroup
                      value={eventSubtypeFilter}
                      exclusive
                      onChange={(_, value) => value && handleEventSubtypeChange(value)}
                      size="small"
                      sx={{ 
                        flexWrap: 'wrap',
                        '& .MuiToggleButton-root': {
                          fontSize: '0.75rem',
                          px: { xs: 1.5, sm: 2 },
                          py: 0.5,
                          minHeight: 32
                        }
                      }}
                    >
                      <ToggleButton value="all">All Event Types</ToggleButton>
                      <ToggleButton value="quant_update">Quant Updates</ToggleButton>
                      <ToggleButton value="meeting">Meetings</ToggleButton>
                      <ToggleButton value="call">Calls</ToggleButton>
                      <ToggleButton value="email">Emails</ToggleButton>
                      <ToggleButton value="other">Other</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
              </Stack>
            </Box>
          </Paper>
        </Collapse>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            View all activities across contacts, initiatives, events, and tasks
          </Typography>
        </Box>

        <Paper 
          sx={{ 
            width: '100%', 
            mb: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <List sx={{ p: 0 }}>
            {filteredActivities.length === 0 ? (
              <ListItem>
                <ListItemText 
                  primary="No activities found" 
                  secondary={typeFilter === 'all' && dateRangeFilter === 'all' && eventSubtypeFilter === 'all'
                    ? 'No activities available' 
                    : 'Try changing the type, date range, or event type filters'} 
                />
              </ListItem>
            ) : (
              filteredActivities.map((activity, idx) => (
                <React.Fragment key={activity.id + activity.timestamp}>
                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => {
                      if (activity.type === 'event' && activity.metadata?.contact_id) {
                        navigate(`/contacts/${activity.metadata.contact_id}`);
                      } else {
                        navigate(`/${activity.type}s/${activity.id}`);
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      {activity.type === 'contact' ? (
                        <PersonIcon sx={{ color: 'primary.main' }} />
                      ) : activity.type === 'initiative' ? (
                        <InitiativeIcon sx={{ color: 'secondary.main' }} />
                      ) : activity.type === 'event' ? (
                        <EventIcon sx={{ color: 'success.main' }} />
                      ) : (
                        <TaskIcon sx={{ color: 'warning.main' }} />
                      )}
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {activity.action}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {activity.name}
                          </Typography>
                          <Chip
                            label={activity.type}
                            size="small"
                            sx={{
                              backgroundColor: 
                                activity.type === 'contact' ? 'primary.light' : 
                                activity.type === 'initiative' ? 'secondary.light' : 
                                activity.type === 'event' ? 'success.light' :
                                'warning.light',
                              color: 
                                activity.type === 'contact' ? 'primary.dark' : 
                                activity.type === 'initiative' ? 'secondary.dark' : 
                                activity.type === 'event' ? 'success.dark' :
                                'warning.dark',
                              fontWeight: 500,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(activity.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton
                      edge="end"
                      onClick={(e) => handleMenuOpen(e, activity)}
                      sx={{ ml: 1, color: 'text.secondary' }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItem>
                  {idx < filteredActivities.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
      </Menu>
    </DashboardLayout>
  );
} 